import { Link } from '@tanstack/react-router'
import { JianLogo } from '@/components/brand/jian-logo'
import './auth-page-shell.css'

interface AuthPageShellProps {
  title: string
  description: string
  footerText: string
  footerActionLabel: string
  footerActionTo: '/login' | '/register'
  showAuthTabs?: boolean
  children: React.ReactNode
}

export function AuthPageShell({
  title,
  description,
  footerText,
  footerActionLabel,
  footerActionTo,
  showAuthTabs = true,
  children,
}: AuthPageShellProps) {
  return (
    <div
      className="auth-page-shell min-h-screen bg-white text-[#101727]"
      data-show-auth-tabs={showAuthTabs}
    >
      <div className="grid min-h-screen lg:grid-cols-[1.08fr_.92fr]">
        <section className="flex min-h-[520px] flex-col overflow-hidden bg-[#0b0d10] px-6 py-8 text-white sm:px-12 lg:min-h-screen lg:px-[clamp(2.5rem,3vw,5rem)]">
          <Link to="/" className="w-max">
            <JianLogo variant="full" size="md" className="[&_*]:!text-white" />
          </Link>

          <div className="mt-16 max-w-2xl lg:mx-auto lg:mt-[7vh] lg:w-full lg:max-w-[72rem]">
            <p className="text-xs font-bold tracking-[.13em] text-[#91e653]">
              AI VIDEO PRODUCTION WORKSPACE
            </p>
            <h1 className="mt-4 text-5xl font-semibold leading-[1.04] tracking-[-.06em] lg:text-6xl xl:text-7xl 2xl:text-[5rem]">
              一条长视频，
              <br />
              变成一组可发布短视频。
            </h1>
            <p className="mt-6 max-w-xl leading-7 text-[#a9afb9]">
              AI 先完成切片、字幕、配音和多版本适配；你再进入专业时间线检查、精修和最终交付。
            </p>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#d8dce2]">
              <span className="border-l-2 border-[#91e653] pl-2">AI 初稿可继续编辑</span>
              <span className="border-l-2 border-[#91e653] pl-2">真实专业时间线</span>
              <span className="border-l-2 border-[#91e653] pl-2">多平台批量输出</span>
            </div>
          </div>

          <div className="relative mt-10 lg:mx-auto lg:w-full lg:max-w-[72rem]">
            <img
              src="/assets/landing/ting.png"
              alt="JIAN 专业编辑器正在剪辑唯美风景视频"
              className="h-48 w-full border border-[#2c3036] object-cover sm:h-64 lg:h-[clamp(20rem,30vw,36rem)]"
            />
            <span className="absolute bottom-3 left-3 border border-[#383d44] bg-black/80 px-3 py-2 text-[11px]">
              AI 初稿已生成 · 随时进入时间线精修
            </span>
          </div>
        </section>

        <section className="flex items-center justify-center px-5 py-12 sm:px-12">
          <div className="w-full max-w-[460px]">
            <div className="mb-10 flex items-center justify-between text-sm">
              <Link to="/" className="text-[#697184] hover:text-[#101727]">
                返回首页
              </Link>
              <span className="text-xs text-[#8c94a3]">账号与项目安全保护</span>
            </div>
            <div className="mb-8 grid grid-cols-2 border-b border-[#e5e7eb] text-center text-sm font-semibold">
              <Link
                to="/login"
                className={
                  footerActionTo === '/register'
                    ? 'border-b-2 border-[#5255f6] pb-3 text-[#101727]'
                    : 'pb-3 text-[#697184]'
                }
              >
                登录
              </Link>
              <Link
                to="/register"
                className={
                  footerActionTo === '/login'
                    ? 'border-b-2 border-[#5255f6] pb-3 text-[#101727]'
                    : 'pb-3 text-[#697184]'
                }
              >
                注册
              </Link>
            </div>
            <h2 className="text-4xl font-semibold tracking-[-.045em]">{title}</h2>
            <p className="mb-8 mt-3 text-sm leading-6 text-[#697184]">{description}</p>

            {children}

            <div className="mt-6 border-t border-[#e5e7eb] pt-6 text-center text-sm text-[#697184]">
              <span>{footerText}</span>{' '}
              <Link to={footerActionTo} className="font-semibold text-[#5255f6] hover:underline">
                {footerActionLabel}
              </Link>
            </div>
            <p className="mt-10 text-center text-[11px] leading-5 text-[#9aa1ae]">
              登录即表示你同意 JIAN 的服务条款与隐私政策。
            </p>
          </div>
        </section>
      </div>
    </div>
  )
}
